import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { collection, addDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "./firebaseConfig";

import { prompts } from "./data/prompts";

import MyPage from "./pages/MyPage";
import SubscribePage from "./pages/SubscribePage";
import SuccessPage from "./pages/SuccessPage";

import InputForm from "./components/InputForm";
import StyleSelector from "./components/StyleSelector";
import OutputCard from "./components/OutputCard";
import LoginButton from "./components/LoginButtons";

function resolveStyle(label) {
  const map = {
    "慈愛の賢者": "benevolent_sage",
    "深淵の思想家": "profound_thinker",
    "陽気な道化師": "cheerful_jester",
    "禅の老師": "zen_master"
  };
  return map[label] || "benevolent_sage";
}

function App() {
  const [user, setUser] = useState(null);
  const [inputText, setInputText] = useState("");
  const [style, setStyle] = useState("慈愛の賢者");
  const [output, setOutput] = useState("");
  const [usageCount, setUsageCount] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const [lastPostId, setLastPostId] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        fetchSubscriberStatus(firebaseUser);
      } else {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const today = new Date().toDateString();
    const saved = JSON.parse(localStorage.getItem("usage") || "{}");
    if (saved.date === today) {
      setUsageCount(saved.count);
      if (saved.count >= 5) setLimitReached(true);
    } else {
      localStorage.setItem("usage", JSON.stringify({ date: today, count: 0 }));
      setUsageCount(0);
      setLimitReached(false);
    }
  }, []);

  const handleConvert = async () => {
    if (!inputText.trim()) return;

    const personaKey = resolveStyle(style);
    const systemPrompt = prompts[personaKey];

    try {
      const response = await fetch("https://api-w3myyghnhq-uc.a.run.app/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          inputText: inputText,
          style: personaKey
        })
      });

      const data = await response.json();
      let converted = data.output || "変換失敗";

      if (personaKey === "sage") converted = converted.replace(/慈愛の.+?者/g, "慈愛の賢者");
      if (personaKey === "thinker") converted = converted.replace(/深淵の[^。！？\s]+(家|者|人)/g, "深淵の思想家");
      if (personaKey === "clown") converted = converted.replace(/陽気な.+?師/g, "陽気な道化師");
      if (personaKey === "zen") converted = converted.replace(/禅の.+?師/g, "禅の老師");

      setOutput(converted);

      if (!user?.isSubscriber) {
        const today = new Date().toDateString();
        const newCount = usageCount + 1;
        localStorage.setItem("usage", JSON.stringify({ date: today, count: newCount }));
        setUsageCount(newCount);
        if (newCount >= 5) {
          setLimitReached(true);
          return;
        }
      }

      if (user) {
        const docRef = await addDoc(collection(db, "posts"), {
          uid: user.uid,
          name: user.displayName,
          input: inputText,
          style: personaKey,
          persona: personaKey,
          output: converted,
          createdAt: new Date()
        });

        setLastPostId(docRef.id);
      }

      // ✅ ここで inputText を消さないようにする
      // setInputText(""); ←削除済み

    } catch (error) {
      console.error("生成AIまたはFirestore保存失敗:", error);
    }
  };

  const handleFavorite = async () => {
    if (!lastPostId) return;
    try {
      await updateDoc(doc(db, "posts", lastPostId), { isFavorite: true });
      alert("お気に入りに追加しました");
    } catch (error) {
      console.error("お気に入り登録失敗:", error);
    }
  };

  const fetchSubscriberStatus = async (firebaseUser) => {
    try {
      const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
      const data = userDoc.data();
      const isSubscriber = data?.isSubscriber || false;

      setUser({
        uid: firebaseUser.uid,
        displayName: firebaseUser.displayName || "匿名",
        email: firebaseUser.email || "",
        isSubscriber
      });
    } catch (error) {
      console.error("灯主ステータス取得失敗:", error);
      setUser({
        uid: firebaseUser.uid,
        displayName: firebaseUser.displayName || "匿名",
        email: firebaseUser.email || "",
        isSubscriber: false
      });
    }
  };

  return (
    <Router>
      <div style={{ padding: "20px", maxWidth: "600px", margin: "0 auto" }}>
        <h1 style={{ textAlign: "center", marginBottom: "30px" }}>ことばのゆるしかた</h1>

        <Routes>
          <Route
            path="/"
            element={
              <>
                <InputForm value={inputText} onChange={setInputText} />
                <StyleSelector value={style} onChange={setStyle} />
                <button
                  onClick={handleConvert}
                  disabled={!user?.isSubscriber && limitReached}
                  style={{
                    marginTop: "10px",
                    padding: "8px 12px",
                    backgroundColor: !user?.isSubscriber && limitReached ? "#eee" : "#e0f7fa",
                    cursor: !user?.isSubscriber && limitReached ? "not-allowed" : "pointer",
                    border: "none",
                  }}
                >
                  供養する
                </button>

                <OutputCard
                  text={output}
                  postId={lastPostId}
                  inputText={inputText}
                  persona={resolveStyle(style)}
                />

                <p style={{ marginTop: "20px", fontSize: "0.9em", color: "#666" }}>
                  {user?.isSubscriber
                    ? "供養回数：無制限（灯主）"
                    : `今日の無料供養残り：${5 - usageCount}回`}
                </p>

                {!user?.isSubscriber && limitReached && (
                  <>
                    <p style={{ marginTop: "20px", color: "#c00" }}>今日の無料供養は終了しました。</p>
                    <Link
                      to="/subscribe"
                      style={{
                        display: "inline-block",
                        marginTop: "10px",
                        padding: "10px 20px",
                        backgroundColor: "#ff6666",
                        color: "#fff",
                        textDecoration: "none",
                        borderRadius: "6px",
                      }}
                    >
                      灯主になる（ゆるしの灯プラン）
                    </Link>
                  </>
                )}

                {user && (
                  <Link
                    to="/mypage"
                    style={{
                      display: "inline-block",
                      marginTop: "30px",
                      padding: "10px 16px",
                      backgroundColor: "#fce4ec",
                      color: "#333",
                      borderRadius: "8px",
                      textDecoration: "none",
                      fontWeight: "bold",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                    }}
                  >
                    灯りの置き場を見る
                  </Link>
                )}

                <div style={{ marginTop: "40px", textAlign: "center" }}>
                  {!user && <LoginButton onLogin={(firebaseUser) => fetchSubscriberStatus(firebaseUser)} />}
                </div>
              </>
            }
          />

          <Route path="/subscribe" element={<SubscribePage />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/mypage" element={<MyPage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
